<template>
  <v-form
    ref="formRef"
    class="wet-pump-form method-form"
    @submit.prevent=""
  >
    <!-- Basisdaten -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-counter</v-icon>
        <span>Basisdaten</span>
      </div>

      <!-- Anzahl -->
      <v-text-field
        v-model.number="form.amount"
        type="number"
        :error-messages="validationErrors?.amount"
        label="Anzahl"
        :hint="hint.amount"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.amount" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.amount }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.amount"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Anzahl übernehmen"
            @click="copyFromReference('amount')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Pumpendaten -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-pump</v-icon>
        <span>Pumpendaten</span>
      </div>

      <!-- Nennweite -->
      <v-autocomplete
        v-model="form.nominalWidth"
        :error-messages="validationErrors?.nominal_width"
        label="Nennweite"
        suffix="mm"
        :items="widths"
        item-title="width"
        item-value="width"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.nominal_width" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.nominal_width }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.nominal_width"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Nennweite übernehmen"
            @click="copyFromReference('nominal_width')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>
    </div>

    <!-- Betriebsstunden -->
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-clock-outline</v-icon>
        <span>Betriebsstunden</span>
      </div>

      <!-- Norm-Betriebsstunden toggle -->
      <v-checkbox
        v-model="form.checkNormUptime"
        :error-messages="validationErrors?.check_norm_uptime"
        label="Norm-Betriebsstunden verwenden"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.check_norm_uptime" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.check_norm_uptime }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.check_norm_uptime"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Norm-Betriebsstunden übernehmen"
            @click="copyFromReference('check_norm_uptime')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-checkbox>

      <!-- Norm-Betriebsstunden selection -->
      <v-autocomplete
        v-if="form.checkNormUptime"
        v-model="form.normUptimeObject"
        label="Norm-Betriebsstunden"
        :items="uptimes"
        item-title="name"
        item-value="id"
        return-object
        :error-messages="validationErrors?.norm_uptime"
        validate-on-blur
        class="mb-3 ml-8"
      >
        <template #append>
          <v-tooltip v-if="hint.norm_uptime" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.norm_uptime }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.norm_uptime"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Norm-Betriebsstunden übernehmen"
            @click="copyFromReference('norm_uptime')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- Manual operating hours -->
      <v-text-field
        v-model.number="form.uptime"
        type="number"
        :error-messages="validationErrors?.uptime"
        label="Jährliche Betriebsstunden"
        suffix="h/a"
        :disabled="form.checkNormUptime"
        :hint="hint.uptime"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.uptime" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.uptime }}</span>
          </v-tooltip>

          <v-btn
            v-if="!reference && referenceData?.uptime"
            icon
            variant="text"
            title="Referenzwert übernehmen"
            aria-label="Referenzwert für Betriebsstunden übernehmen"
            @click="copyFromReference('uptime')"
          >
            <v-icon>mdi-content-copy</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Regelung: reference only -->
    <div v-if="reference" class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-tune-variant</v-icon>
        <span>Regelung</span>
      </div>

      <v-autocomplete
        v-model="form.regulation"
        :error-messages="validationErrors?.regulation"
        label="Regelung"
        :items="regulations"
        item-title="text"
        item-value="value"
        validate-on-blur
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.regulation" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>

            <span>{{ hint.regulation }}</span>
          </v-tooltip>
        </template>
      </v-autocomplete>
    </div>
  </v-form>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import api from '@/plugins/axios'
import { djangoChoicesToVuetify } from '@/utils'
import { useBaseForm } from '@/composables/useBaseForm'

/* Props (match action.vue contract) */
const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  reference:  { type: Boolean, default: false },
  planned:    { type: Boolean, default: false },
  otherData:  { type: Object, default: () => ({}) },
  sharedData: { type: Object, default: () => ({}) },
  validationErrors: { type: Object, default: () => ({}) },
  methodValue: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue'])

/* Field mapping (frontend -> backend keys) */
const fields = {
  amount:           'amount',
  uptime:           'uptime',
  nominalWidth:     'nominal_width',
  regulation:       'regulation',
  checkNormUptime:  'check_norm_uptime',
  normUptimeObject: 'norm_uptime',
}

/* useBaseForm */
const {
  form,
  hint,
  referenceData,
  copyFromReference,
  fetchHints,
} = useBaseForm({
  value: computed(() => props.modelValue),
  fields,
  defaults: { checkNormUptime: false },
  reference: computed(() => props.reference),
  planned:   computed(() => props.planned),
  shared:    computed(() => false),
  otherData: computed(() => props.otherData),
  sharedData:computed(() => props.sharedData),
  methodValue: computed(() => props.methodValue),
  emit: (e, p) => emit(e, p),
})

/* Local choices & UI state */
const uptimes = ref([])
const regulations = ref([])
const widths = ref([])

/* Derive uptime from selected norm uptime option */
const normUptime = computed(
  () => form.normUptimeObject?.norm_uptime ?? null
)

watch(normUptime, (val) => {
  if (val != null) {
    form.uptime = val
  }
})

watch(
  () => form.checkNormUptime,
  (enabled) => {
    if (!enabled) {
      form.normUptimeObject = null
    }
  }
)

/* Fetchers (updated endpoints; keep cacheAdapter) */
async function fetchNormUptimes() {
  try {
    const { data } = await api.get('/methods/wetpump1/uptime/')
    uptimes.value = data
  } catch { /* optional: toast */ }
}

async function fetchRegulations() {
  try {
    const { data } = await api.get('/methods/wetpump1/regulation/', {
      params: { planned: props.planned || null, reference: props.reference || null },
    })
    regulations.value = djangoChoicesToVuetify(data) // -> [{ text, value }]
  } catch {}
}

async function fetchWidths() {
  try {
    const { data } = await api.get('/methods/wetpump1/nominal-width/')
    widths.value = data // -> [{ width }, ...]
  } catch {}
}

/* Expose validate for parent (save error highlighting) */
const formRef = ref(null)
function validateForm() {
  return formRef.value?.validate?.()
}

defineExpose({
  validateForm,
  fields: Object.values(fields),
})

/* Lifecycle */
onMounted(async () => {
  await Promise.all([fetchNormUptimes(), fetchRegulations(), fetchWidths()])
  await fetchHints()
})
</script>

<style scoped lang="scss">
.wet-pump-form {
  width: 100%;
}

/* Input fields */
.wet-pump-form :deep(.v-field) {
  border-radius: 18px;
}

.wet-pump-form :deep(.v-input) {
  margin-bottom: 0.35rem;
}

/* Visual sections */
.method-section {
  margin-bottom: 1.25rem;
  padding: 1rem;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
  border-radius: 24px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

.method-section:last-child {
  margin-bottom: 0;
}

/* Section headings */
.method-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;
  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  border-radius: 18px;
  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);
  font-size: 1rem;
  font-weight: 700;
}

/* Checkbox */
.wet-pump-form :deep(.v-checkbox) {
  padding: 0.35rem 0.65rem;
  border-radius: 18px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

/* Nested norm-uptime field */
.wet-pump-form :deep(.ml-8) {
  margin-left: 1.5rem !important;
  padding-left: 1rem;
  border-left: 2px solid rgba(var(--v-theme-primary), 0.12);
}

/* Help and reference-copy actions */
.wet-pump-form :deep(.v-input__append),
.wet-pump-form :deep(.v-field__append-inner) {
  align-items: center;
}

.wet-pump-form :deep(.v-input__append .v-icon),
.wet-pump-form :deep(.v-field__append-inner .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

.wet-pump-form :deep(.v-input__append .v-btn),
.wet-pump-form :deep(.v-field__append-inner .v-btn) {
  border-radius: 999px;
  color: rgb(var(--v-theme-primary));
}

.wet-pump-form :deep(.v-input__append .v-btn:hover),
.wet-pump-form :deep(.v-field__append-inner .v-btn:hover) {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* Disabled states */
.wet-pump-form :deep(.v-field--disabled) {
  opacity: 0.72;
}

@media (max-width: 700px) {
  .method-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .method-section-heading {
    align-items: flex-start;
    font-size: 0.95rem;
  }

  .wet-pump-form :deep(.ml-8) {
    margin-left: 0.75rem !important;
    padding-left: 0.75rem;
  }
}
</style>

